import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lockup } from "./Lockup";
import { UserMenu } from "./UserMenu";
import { useSession } from "../lib/auth";
import { useT, useLp, useLangSwitcher } from "../lib/i18n";
import { type Lang } from "../lib/translations";

const LANG_OPTIONS: { lang: Lang; flag: string; label: string }[] = [
  { lang: "en", flag: "🇺🇸", label: "English" },
  { lang: "pt", flag: "🇧🇷", label: "Português" },
  { lang: "es", flag: "🇪🇸", label: "Español" },
];

function LangSelect() {
  const { lang, switchTo } = useLangSwitcher();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANG_OPTIONS.find((o) => o.lang === lang) ?? LANG_OPTIONS[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Select language"
        className="flex items-center gap-1.5 rounded-lg border border-ink/12 dark:border-paper/12 px-2.5 py-1.5 text-sm text-ink/70 dark:text-paper/70 hover:border-ink/25 dark:hover:border-paper/25 hover:text-ink dark:hover:text-paper transition-colors bg-transparent"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="font-mono text-[11px] uppercase tracking-wide">{current.lang}</span>
        <svg
          className={`h-3 w-3 transition-transform duration-150 opacity-50 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="2,4 6,8 10,4" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[150px] rounded-xl border border-ink/10 dark:border-paper/10 bg-paper dark:bg-ink shadow-lg shadow-ink/10 dark:shadow-black/30 overflow-hidden py-1">
          {LANG_OPTIONS.map((opt) => (
            <button
              key={opt.lang}
              onClick={() => { switchTo(opt.lang); setOpen(false); }}
              className={[
                "flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors",
                opt.lang === lang
                  ? "text-ink dark:text-paper bg-ink/5 dark:bg-paper/5 font-medium"
                  : "text-ink/60 dark:text-paper/60 hover:bg-ink/5 dark:hover:bg-paper/5 hover:text-ink dark:hover:text-paper",
              ].join(" ")}
            >
              <span className="text-base leading-none">{opt.flag}</span>
              <span>{opt.label}</span>
              {opt.lang === lang && (
                <svg className="ml-auto h-3.5 w-3.5 text-graphite" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="2,7 6,11 12,3" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <circle cx="10" cy="7" r="3.25" />
      <path d="M3.5 17c0-3.038 2.91-5.5 6.5-5.5s6.5 2.462 6.5 5.5" />
    </svg>
  );
}

export function Header() {
  const session = useSession();
  const t = useT();
  const lp = useLp();
  // Sticky header grows a translucent bg + border once the user scrolls past
  // the hero padding. Above the fold it stays transparent so the hero's
  // paper-ruled pattern reads cleanly.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={[
        "sticky top-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-300",
        scrolled
          ? "bg-paper/75 dark:bg-ink/75 backdrop-blur-md border-b border-ink/8 dark:border-paper/8"
          : "bg-transparent border-b border-transparent",
      ].join(" ")}
    >
      <div className="mx-auto max-w-6xl px-4 py-5 flex items-center gap-3 sm:px-6 sm:gap-6">
      {/* Logo */}
      <Link to={lp + "/"} aria-label="Minuta" className="block shrink-0">
        <Lockup className="h-7 w-auto" />
      </Link>

      {/* Nav */}
      <nav className="hidden md:flex items-center gap-7 text-sm text-ink/65 dark:text-paper/65 flex-1">
        <a href={lp + "/#copilot"} className="hover:text-ink dark:hover:text-paper transition-colors">
          {t.nav.copilot}
        </a>
        <a href={lp + "/#how-it-works"} className="hover:text-ink dark:hover:text-paper transition-colors">
          {t.nav.howItWorks}
        </a>
        <Link to={lp + "/pricing"} className="hover:text-ink dark:hover:text-paper transition-colors">
          {t.nav.pricing}
        </Link>
        <a href={lp + "/#faq"} className="hover:text-ink dark:hover:text-paper transition-colors">
          {t.nav.faq}
        </a>
        <Link to={lp + "/blog"} className="hover:text-ink dark:hover:text-paper transition-colors">
          {t.nav.blog}
        </Link>
      </nav>

      {/* Right side actions */}
      <div className="flex items-center gap-2 shrink-0 ml-auto">
        {session ? (
          <UserMenu />
        ) : (
          <>
            {/* Login — user icon */}
            <Link
              to={lp + "/?auth=login"}
              aria-label={t.nav.signIn}
              title={t.nav.signIn}
              className="hidden md:inline-flex items-center justify-center h-9 w-9 rounded-lg text-ink/60 dark:text-paper/60 hover:text-ink dark:hover:text-paper hover:bg-ink/6 dark:hover:bg-paper/6 transition-colors"
            >
              <UserIcon />
            </Link>

            {/* CTA */}
            <Link
              to={lp + "/?auth=signup&plan=free"}
              className="inline-flex items-center rounded-full bg-ink px-3 py-2 text-sm font-medium text-paper hover:bg-ink/85 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90 sm:px-4"
            >
              {t.nav.getStarted}
            </Link>
          </>
        )}

        {/* Divider */}
        <span className="hidden sm:block h-5 w-px bg-ink/12 dark:bg-paper/12 mx-1" />

        {/* Language selector */}
        <LangSelect />
      </div>
      </div>
    </header>
  );
}
